/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useMemo } from 'react';
import type { ShopperProducts } from '@/scapi';
import type { GalleryImage } from '@/components/image-gallery';
import { widenImageUrl, widenImageSrcSet, widenVideoUrl } from '@/lib/images/widen-image';

/**
 * Build the PDP gallery media list from the product record's Widen fields (UCR-2697):
 * main image (`pdpMainImage`), up to 25 alternates (`pdpAltImage1…25`), and an optional video
 * (`videoLink` + `videoThumbnailDAM`). These are Widen CDN URLs served directly (no DIS) — the
 * returned items carry `external`/`srcSet` so `<ImageGallery>` renders them as plain responsive
 * `<img>`/`<video>` rather than through `<DynamicImage>`.
 *
 * Distinct from `useProductImages` (the DIS / `imageGroups` path still used elsewhere).
 */
const ALT_IMAGE_COUNT = 25;
const THUMB_WIDTH = 160;
// Main image occupies the full container below lg, half at lg+ (the 2-col PDP layout).
const MAIN_SIZES = '(min-width: 1024px) 50vw, 100vw';
const MAIN_WIDTHS = [400, 600, 800, 1024, 1280] as const;

function readString(product: ShopperProducts.schemas['Product'], key: string): string | undefined {
    const value = (product as Record<string, unknown>)[key];
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function imageItem(url: string): GalleryImage {
    return {
        src: widenImageUrl(url, { w: 800 }),
        srcSet: widenImageSrcSet(url, MAIN_WIDTHS),
        sizes: MAIN_SIZES,
        thumbSrc: widenImageUrl(url, { w: THUMB_WIDTH }),
        external: true,
    };
}

/**
 * @param product - the product whose Widen gallery fields to read.
 *
 * Images carry no `alt`: the gallery is a decorative, `aria-hidden` widget (the product name already
 * labels the page for assistive tech), so `<ImageGallery>` renders every image with an empty `alt`.
 */
export function useProductGalleryMedia(product: ShopperProducts.schemas['Product']): { media: GalleryImage[] } {
    const media = useMemo<GalleryImage[]>(() => {
        const items: GalleryImage[] = [];

        const main = readString(product, 'c_pdpMainImage');
        if (main) items.push(imageItem(main));

        for (let i = 1; i <= ALT_IMAGE_COUNT; i++) {
            const url = readString(product, `c_pdpAltImage${i}`);
            if (url) items.push(imageItem(url));
        }

        const videoLink = readString(product, 'c_videoLink');
        if (videoLink) {
            const poster = readString(product, 'c_videoThumbnailDAM') ?? main;
            items.push({
                src: poster ? widenImageUrl(poster, { w: 800 }) : '',
                srcSet: poster ? widenImageSrcSet(poster, MAIN_WIDTHS) : undefined,
                sizes: MAIN_SIZES,
                thumbSrc: poster ? widenImageUrl(poster, { w: THUMB_WIDTH }) : '',
                external: true,
                videoSrc: widenVideoUrl(videoLink),
            });
        }

        return items;
    }, [product]);

    return { media };
}
